import {
  type ApiResponse,
  type PaginatedResponse
} from '../../api-client';

import {
  apiRequest
} from './client';

import type {
  CreateDonationInput,
  CreateEftDonationInput,
  Donation,
  DonationStatus,
  InitiateDonationResult,
  InitiateEftDonationResult,
  PresignResult,
  VerifyDonationInput
} from './types/donations.types';

/** Public - no auth. Creates a donation and returns a gateway redirect URL. */
export async function initiateDonation(
  input: CreateDonationInput
): Promise<ApiResponse<InitiateDonationResult>> {

  return apiRequest<ApiResponse<InitiateDonationResult>>(
    '/donations',
    {
      method: 'POST',
      body: JSON.stringify(input)
    }
  );
}

/** Public - no auth. Creates an EFT donation and returns a bank reference (no redirect). */
export async function initiateEftDonation(
  input: CreateEftDonationInput
): Promise<ApiResponse<InitiateEftDonationResult>> {

  return apiRequest<ApiResponse<InitiateEftDonationResult>>(
    '/donations/eft',
    {
      method: 'POST',
      body: JSON.stringify(input)
    }
  );
}

/** Public - no auth, reCAPTCHA-gated on the backend via recaptchaToken in the body. */
export async function presignDonationProof(
  donationId: number,
  input: { filename: string
    mimeType: string
    size: number
    recaptchaToken: string }
): Promise<ApiResponse<PresignResult>> {

  return apiRequest<ApiResponse<PresignResult>>(
    `/donations/${ donationId }/proof/presign`,
    {
      method: 'POST',
      body: JSON.stringify(input)
    }
  );
}

/** Public - no auth, reCAPTCHA-gated on the backend via recaptchaToken in the body. */
export async function confirmDonationProof(
  donationId: number,
  input: { fileKey: string
    recaptchaToken: string }
): Promise<ApiResponse<Donation>> {

  return apiRequest<ApiResponse<Donation>>(
    `/donations/${ donationId }/proof/confirm`,
    {
      method: 'POST',
      body: JSON.stringify(input)
    }
  );
}

export interface ListDonationsParams {
  status?: DonationStatus
  limit?: number
  offset?: number
}

/** Admin only. */
export async function listDonations(
  params: ListDonationsParams = {},
  token: string
): Promise<PaginatedResponse<Donation>> {

  const query = new URLSearchParams();

  if (params.status !== undefined) {

    query.set('status', params.status);
  }

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';
  return apiRequest<PaginatedResponse<Donation>>(`/donations${ qs }`, undefined, token);
}

/** Admin only. */
export async function getDonationById(
  id: number,
  token: string
): Promise<ApiResponse<Donation>> {

  return apiRequest<ApiResponse<Donation>>(`/donations/${ id }`, undefined, token);
}

/** Admin only. */
export async function getDonationProofDownloadUrl(
  id: number,
  token: string
): Promise<ApiResponse<{ downloadUrl: string }>> {

  return apiRequest<ApiResponse<{ downloadUrl: string }>>(`/donations/${ id }/proof/download`, undefined, token);
}

/** Admin only. */
export async function verifyDonation(
  id: number,
  input: VerifyDonationInput,
  token: string
): Promise<ApiResponse<Donation>> {

  return apiRequest<ApiResponse<Donation>>(
    `/donations/${ id }/verify`,
    {
      method: 'PATCH',
      body: JSON.stringify(input)
    },
    token
  );
}
